import { Feature, Point } from 'geojson';
import { MapMouseEvent } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useRef } from 'react';
import ExploreMapPinType from '@alltrails/amplitude/enums/ExploreMapPinType';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { MapLayerInteractionsContext } from '../components/MapLayerInteractionsProvider';
import TrailResultProperties from '../components/TrailResults/TrailResultProperties';
import { useAppDispatch, useAppSelector } from '../redux';
import { addViewedPinSerializedCoordinatesThisSession, updateActiveTrails, updateCursor } from '../redux/reducer';
import { collapsedTrailPinsActiveId, collapsedTrailPinsId, regularTrailPinsId } from '../utils/layers';

export default function useTrailPinInteractions(
  selectedSerializedCoordinates: string,
  setHoveredSerializedCoordinates: (serializedCoordinates: string) => void,
  setSelectedSerializedCoordinates: (serializedCoordinates: string) => void,
  isFavorite?: (itemId: number) => boolean,
  isComplete?: (itemId: number) => boolean,
  cardLocation?: TrailCardLocation,
  mapLocation?: MapLocation | string, // Accept string for legacy PageStrings support
  onPinHover?: (trailIds: number[]) => void,
  onSingleTrailPinClick?: (trail: TrailSearchResult, event: MouseEvent) => void
) {
  const mapLocationForAnalytics = mapLocation as MapLocation | undefined;
  const dispatch = useAppDispatch();
  const { allowClickEvents, trailResults } = useAppSelector(state => state.map);
  const isMobile = useIsMobileSizedScreen();

  const getPinType = useCallback(
    (trails: TrailSearchResult[]) => {
      let pinType: ExploreMapPinType = 'trail';
      if (trails.length > 1) {
        pinType = 'trailhead';
      }
      if (trails.every(t => t.is_closed)) {
        pinType = 'closed';
      } else if (isFavorite && trails.every(t => isFavorite(t.ID))) {
        pinType = 'saved';
      } else if (isComplete && trails.every(t => isComplete(t.ID))) {
        pinType = 'completed';
      }

      return pinType;
    },
    [isComplete, isFavorite]
  );

  const lastHoveredTrailId = useRef<number | undefined | null>(null);
  const lastClickedSerializedCoordinatesRef = useRef<string>('');

  const onHover = useCallback(
    (properties?: TrailResultProperties) => {
      // We don't bother passing in properties when a user hovers on the trail card, since no changes need to be dispatched in that case
      if (properties) {
        // We must use the serialized coordinates that we assigned to the point rather than constructing the string from the coordinates
        // on the mapbox event because for some (still unknown) reason, the mapbox coordinates were the slightest bit off...
        const serializedCoordinates = properties.serializedCoordinates;
        setHoveredSerializedCoordinates(serializedCoordinates);

        // Don't let hover overwrite activeTrails when we're over the pin that was just clicked (click and hover can fire in the same tick).
        if (serializedCoordinates === lastClickedSerializedCoordinatesRef.current) {
          return;
        }
        if (selectedSerializedCoordinates) {
          if (serializedCoordinates === selectedSerializedCoordinates) {
            return;
          } else {
            setSelectedSerializedCoordinates('');
          }
        }
        const hoveredTrails = trailResults?.[serializedCoordinates]?.filter(trail => Boolean(trail));
        dispatch(updateActiveTrails(hoveredTrails));
        dispatch(updateCursor('pointer'));

        if (hoveredTrails) {
          onPinHover?.(hoveredTrails.map(trail => trail.ID));
          const trailId = hoveredTrails.length === 1 ? hoveredTrails[0].ID : undefined;
          const pinType = getPinType(hoveredTrails);
          // This function gets called on mouse move so we want to only fire it once per trail pin
          if (lastHoveredTrailId.current !== trailId) {
            lastHoveredTrailId.current = trailId;
            if (cardLocation) {
              logExploreMapPinSelected({
                action_taken: 'hovered on explore map',
                pin_type: pinType,
                trail_id: trailId,
                map_location: mapLocationForAnalytics
              });
            }
          }
        }
      }
    },
    [
      dispatch,
      onPinHover,
      selectedSerializedCoordinates,
      trailResults,
      cardLocation,
      mapLocationForAnalytics,
      getPinType,
      setHoveredSerializedCoordinates,
      setSelectedSerializedCoordinates
    ]
  );

  const onHoverEnd = useCallback(() => {
    setHoveredSerializedCoordinates('');
    lastClickedSerializedCoordinatesRef.current = '';
    onPinHover?.([]);
    // We don't want to clear activeTrails if a trail pin has been selected - that will get taken care of when the pin is de-selected
    if (!selectedSerializedCoordinates) {
      dispatch(updateActiveTrails(undefined));
    }
    dispatch(updateCursor('unset'));
  }, [dispatch, onPinHover, selectedSerializedCoordinates, setHoveredSerializedCoordinates]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true);
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (onMouseEnter && event.features) {
        const marker = event.features[0] as unknown as Feature<Point, TrailResultProperties>;
        onMouseEnter(marker.properties);
      }
    },
    [onMouseEnter]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        event.preventDefault(); // Allows us to detect when a user clicks on the map but NOT on this layer
        const marker = event.features[0] as unknown as Feature<Point, TrailResultProperties>;
        if (trailResults) {
          const serializedCoordinates = marker.properties.serializedCoordinates;
          lastClickedSerializedCoordinatesRef.current = serializedCoordinates;

          const trailsAtCoordinates = trailResults[serializedCoordinates];
          const selectedTrail = trailsAtCoordinates[0];
          // Desktop already previews the card on hover, so a click can go straight to the caller. Mobile has no hover, so a
          // tap still shows the card, and a pin with several trails still opens its carousel.
          const isHandedToCaller = Boolean(onSingleTrailPinClick) && !isMobile && trailsAtCoordinates.length === 1;
          if (!isHandedToCaller) {
            setSelectedSerializedCoordinates(serializedCoordinates);
          }
          if (selectedTrail) {
            dispatch(addViewedPinSerializedCoordinatesThisSession(serializedCoordinates));
            if (!isHandedToCaller) {
              dispatch(updateActiveTrails([selectedTrail]));
            }
            const pinType = getPinType(trailsAtCoordinates);
            if (cardLocation) {
              logExploreMapPinSelected({
                action_taken: 'clicked on explore map',
                pin_type: pinType,
                trail_id: selectedTrail.ID,
                map_location: mapLocationForAnalytics
              });
            }
            if (isHandedToCaller) {
              onSingleTrailPinClick?.(selectedTrail, event.originalEvent);
            }
          }
        }
      }
    },
    [cardLocation, mapLocationForAnalytics, dispatch, getPinType, isMobile, onSingleTrailPinClick, setSelectedSerializedCoordinates, trailResults]
  );

  useEffect(() => {
    if (!allowClickEvents) {
      return;
    }

    // Register event handlers for both regular and collapsed trail pin layers
    registerEventHandler(regularTrailPinsId, 'click', onClick);
    registerEventHandler(collapsedTrailPinsId, 'click', onClick);
    registerEventHandler(collapsedTrailPinsActiveId, 'click', onClick);

    if (!isMobile) {
      registerEventHandler(regularTrailPinsId, 'mousemove', onMouseMove);
      registerEventHandler(collapsedTrailPinsId, 'mousemove', onMouseMove);
      registerEventHandler(collapsedTrailPinsActiveId, 'mousemove', onMouseMove);

      if (onMouseLeave) {
        registerEventHandler(regularTrailPinsId, 'mouseleave', onMouseLeave);
        registerEventHandler(collapsedTrailPinsId, 'mouseleave', onMouseLeave);
        registerEventHandler(collapsedTrailPinsActiveId, 'mouseleave', onMouseLeave);
      }
    }

    return () => {
      unregisterEventHandler(regularTrailPinsId, 'click', onClick);
      unregisterEventHandler(collapsedTrailPinsId, 'click', onClick);
      unregisterEventHandler(collapsedTrailPinsActiveId, 'click', onClick);
      unregisterEventHandler(regularTrailPinsId, 'mousemove', onMouseMove);
      unregisterEventHandler(collapsedTrailPinsId, 'mousemove', onMouseMove);
      unregisterEventHandler(collapsedTrailPinsActiveId, 'mousemove', onMouseMove);

      if (onMouseLeave) {
        unregisterEventHandler(regularTrailPinsId, 'mouseleave', onMouseLeave);
        unregisterEventHandler(collapsedTrailPinsId, 'mouseleave', onMouseLeave);
        unregisterEventHandler(collapsedTrailPinsActiveId, 'mouseleave', onMouseLeave);
      }
    };
  }, [allowClickEvents, isMobile, onClick, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);
  return {
    onMouseEnter,
    onMouseLeave
  };
}
